import * as THREE from 'three';
import { box, weld } from '../level/VoxelFactory.js';
import { eventBus, Events } from '../core/EventBus.js';
import { ROW_DEPTH, COLORS, ROW, PLAYER, JUICE } from '../core/Constants.js';
import { gameState } from '../core/GameState.js';
import { DeathCause } from './CollisionSystem.js';

const S = COLORS.SKIN;
const H = COLORS.HAIR;
const T = COLORS.SHIRT;
const P = COLORS.TROUSERS;
const SHOE = COLORS.SHOE;
const BLOOD_CRIMSON = 0xdc143c;
const BLOOD_DARK = 0x6e0606;
const BLOOD_BRIGHT = 0xff1e1e;
const BLOOD_DEEP = 0x4a0303;

function createBodyPartGeometries() {
  return [
    // 0: Head (skin, hair, neck, bloody neck stump)
    weld([
      box(0.32, 0.14, 0.32, S, 0, -0.35, 0),
      box(0.78, 0.58, 0.68, S, 0, 0, 0),
      box(0.86, 0.20, 0.76, H, 0, 0.35, 0),
      box(0.86, 0.45, 0.16, H, 0, 0.08, 0.32),
      box(0.10, 0.40, 0.72, H, -0.42, 0.06, 0),
      box(0.10, 0.40, 0.72, H, 0.42, 0.06, 0),
      box(0.30, 0.14, 0.30, BLOOD_DARK, 0, -0.44, 0),
    ]),
    // 1: Torso (blue shirt chest + pants waist + bloody torn stumps)
    weld([
      box(1.02, 0.70, 0.56, T, 0, 0.2, 0),
      box(1.02, 0.28, 0.52, P, 0, -0.28, 0),
      box(0.34, 0.14, 0.34, BLOOD_DARK, 0, 0.60, 0),
      box(0.24, 0.22, 0.34, BLOOD_CRIMSON, -0.55, 0.38, 0),
      box(0.24, 0.22, 0.34, BLOOD_CRIMSON, 0.55, 0.38, 0),
      box(0.94, 0.12, 0.46, BLOOD_DEEP, 0, -0.46, 0),
    ]),
    // 2: Left Arm
    weld([
      box(0.28, 0.55, 0.34, T, 0, 0.12, 0),
      box(0.26, 0.22, 0.34, S, 0, -0.26, 0),
      box(0.26, 0.10, 0.30, BLOOD_BRIGHT, 0, 0.42, 0),
    ]),
    // 3: Right Arm
    weld([
      box(0.28, 0.55, 0.34, T, 0, 0.12, 0),
      box(0.26, 0.22, 0.34, S, 0, -0.26, 0),
      box(0.26, 0.10, 0.30, BLOOD_BRIGHT, 0, 0.42, 0),
    ]),
    // 4: Left Leg
    weld([
      box(0.38, 0.64, 0.42, P, 0, 0, 0),
      box(0.34, 0.10, 0.38, BLOOD_DARK, 0, 0.36, 0),
      box(0.34, 0.10, 0.38, BLOOD_CRIMSON, 0, -0.36, 0),
    ]),
    // 5: Right Leg
    weld([
      box(0.38, 0.64, 0.42, P, 0, 0, 0),
      box(0.34, 0.10, 0.38, BLOOD_DARK, 0, 0.36, 0),
      box(0.34, 0.10, 0.38, BLOOD_CRIMSON, 0, -0.36, 0),
    ]),
    // 6: Left Shoe
    weld([
      box(0.42, 0.18, 0.58, SHOE, 0, 0, 0),
      box(0.36, 0.08, 0.44, BLOOD_CRIMSON, 0, 0.12, 0),
    ]),
    // 7: Right Shoe
    weld([
      box(0.42, 0.18, 0.58, SHOE, 0, 0, 0),
      box(0.36, 0.08, 0.44, BLOOD_CRIMSON, 0, 0.12, 0),
    ]),
  ];
}

function createBloodGeometries() {
  return {
    chunks: [
      weld([box(0.38, 0.38, 0.38, BLOOD_CRIMSON)]),
      weld([box(0.48, 0.28, 0.48, BLOOD_DARK)]),
      weld([box(0.28, 0.48, 0.28, BLOOD_BRIGHT)]),
      weld([box(0.55, 0.22, 0.38, BLOOD_DEEP)]),
    ],
    sprays: [
      weld([box(0.22, 0.22, 0.22, BLOOD_BRIGHT)]),
      weld([box(0.16, 0.16, 0.16, BLOOD_CRIMSON)]),
      weld([box(0.12, 0.12, 0.12, BLOOD_DARK)]),
    ],
    splats: [
      weld([
        box(1.2, 0.02, 0.7, BLOOD_CRIMSON, 0, 0, 0),
        box(0.6, 0.02, 0.4, BLOOD_DARK, 0.3, 0, -0.1),
      ]),
      weld([
        box(1.6, 0.02, 0.8, BLOOD_DARK, 0, 0, 0),
        box(0.8, 0.02, 0.9, BLOOD_CRIMSON, -0.3, 0, 0.2),
      ]),
      weld([
        box(2.4, 0.02, 1.0, BLOOD_DEEP, 0, 0, 0),
        box(1.4, 0.02, 0.6, BLOOD_CRIMSON, 0.4, 0, 0),
      ]),
    ],
  };
}

const SMOKE_DARK = 0x1e293b;
const SMOKE_MID = 0x475569;
const SMOKE_LIGHT = 0x94a3b8;
const ELECTRIC_SPARK = 0x7dd3fc;
const SCORCH_BLACK = 0x0f172a;

function createSmokeGeometries() {
  return {
    clouds: [
      weld([box(0.55, 0.55, 0.55, SMOKE_DARK)]),
      weld([box(0.65, 0.45, 0.65, SMOKE_MID)]),
      weld([box(0.42, 0.52, 0.42, SMOKE_LIGHT)]),
      weld([box(0.38, 0.38, 0.38, ELECTRIC_SPARK)]),
    ],
    sparks: [
      weld([box(0.24, 0.24, 0.24, ELECTRIC_SPARK)]),
      weld([box(0.18, 0.18, 0.18, SMOKE_MID)]),
      weld([box(0.14, 0.14, 0.14, 0xffffff)]),
    ],
    scorches: [
      weld([
        box(1.4, 0.02, 0.8, SCORCH_BLACK, 0, 0, 0),
        box(0.7, 0.02, 0.5, SMOKE_DARK, 0.2, 0, -0.1),
      ]),
      weld([
        box(1.8, 0.02, 1.0, SCORCH_BLACK, 0, 0, 0),
        box(0.9, 0.02, 0.7, SMOKE_MID, -0.3, 0, 0.2),
      ]),
    ],
  };
}

/**
 * Manages physics-driven body parts and explosive blood splatter on train impact.
 */
export class ParticleSystem {
  constructor(scene) {
    this.scene = scene;
    this.bodyPartGeometries = createBodyPartGeometries();
    this.bloodGeometries = createBloodGeometries();
    this.smokeGeometries = createSmokeGeometries();
    this.material = new THREE.MeshLambertMaterial({ vertexColors: true });

    this.group = new THREE.Group();
    scene.add(this.group);

    this.parts = [];

    this.unsub = [
      eventBus.on(Events.BURST, (d) => this.burst(d)),
      eventBus.on('cobra:hiss', (d) => this.rustle(d)),
      eventBus.on('water:splash', (d) => this.splash(d)),
      eventBus.on('zap:spark', (d) => this.sparks(d)),
    ];
  }

  sparks({ x, z }) {
    if (this.parts.length >= JUICE.GORE_MAX_PARTS) return;
    for (let i = 0; i < 6; i++) {
      const geo = this.bloodGeometries.sprays[i % this.bloodGeometries.sprays.length];
      const mesh = new THREE.Mesh(geo, this.material);
      mesh.position.set(
        x + (Math.random() - 0.5) * 2.0,
        0.2 + Math.random() * 0.4,
        z + (Math.random() - 0.5) * 0.8,
      );
      const vx = (Math.random() - 0.5) * 4.0;
      const vy = 1.5 + Math.random() * 3.0;
      const vz = (Math.random() - 0.5) * 4.0;
      this.group.add(mesh);
      this.parts.push({
        mesh,
        vx, vy, vz,
        spinX: (Math.random() - 0.5) * 25,
        spinY: (Math.random() - 0.5) * 25,
        spinZ: 0,
        groundY: 0,
        active: true,
        type: 'spray',
      });
    }
  }

  splash({ x, z }) {
    for (let i = 0; i < 4; i++) {
      const geo = this.bloodGeometries.sprays[i % this.bloodGeometries.sprays.length];
      const mesh = new THREE.Mesh(geo, this.material);
      mesh.position.set(
        x + (Math.random() - 0.5) * 3.5,
        0.4 + Math.random() * 1.2,
        z + (Math.random() - 0.5) * 4.0,
      );
      const vx = (Math.random() - 0.5) * 4.0;
      const vy = 2.0 + Math.random() * 3.5;
      const vz = (Math.random() - 0.5) * 4.0;
      this.group.add(mesh);
      this.parts.push({
        mesh,
        vx, vy, vz,
        spinX: (Math.random() - 0.5) * 15,
        spinY: (Math.random() - 0.5) * 15,
        spinZ: 0,
        groundY: 0,
        active: true,
        type: 'splash',
        life: 0,
        maxLife: 0.4 + Math.random() * 0.25,
      });
    }

    while (this.parts.length > 150) {
      const oldest = this.parts.shift();
      oldest.mesh.removeFromParent();
    }
  }

  rustle({ x, z }) {
    if (this.parts.length >= JUICE.GORE_MAX_PARTS) return;
    for (let i = 0; i < 20; i++) {
      const geo = this.bloodGeometries.sprays[i % this.bloodGeometries.sprays.length];
      const mesh = new THREE.Mesh(geo, this.material);
      mesh.position.set(
        x + (Math.random() - 0.5) * 1.6,
        0.1 + Math.random() * 0.3,
        z + (Math.random() - 0.5) * 1.6,
      );
      const ang = Math.random() * Math.PI * 2;
      const spd = 1.2 + Math.random() * 3.0;
      const vx = Math.cos(ang) * spd;
      const vy = 2.0 + Math.random() * 4.0;
      const vz = Math.sin(ang) * spd;
      this.group.add(mesh);
      this.parts.push({
        mesh,
        vx, vy, vz,
        spinX: (Math.random() - 0.5) * 20,
        spinY: (Math.random() - 0.5) * 20,
        spinZ: 0,
        groundY: 0,
        active: true,
        type: 'spray',
      });
    }
  }

  burst({ x, row, cause, lane, consist }) {
    if (cause === DeathCause.COBRA) {
      // Snake bite has NO GORE! Player turns green then becomes a skeleton.
      return;
    }

    const z = -row * ROW_DEPTH;
    const isFreight = lane && lane.type === ROW.FREIGHT;
    const groundY = isFreight ? PLAYER.DECK_Y : PLAYER.GROUND_Y;

    const isStruck = cause === DeathCause.STRUCK;
    const isElectrocuted = cause === DeathCause.ELECTROCUTED || cause === DeathCause.PIT;
    const dir = lane ? lane.dir : (Math.random() < 0.5 ? 1 : -1);
    const speed = lane ? lane.speed : 12;

    const trainVx = isStruck ? dir * speed : 0;

    let impactX = x;
    if (isStruck && consist) {
      impactX = dir > 0
        ? Math.max(x, consist.max - 0.4)
        : Math.min(x, consist.min + 0.4);
    }

    const impactY = groundY + 1.1;
    const impactZ = z;

    if (isElectrocuted) {
      // 1. SPAWN RISING SMOKE CLOUDS & ELECTRIC SPARKS (NO BLOOD!)
      const smokeCount = 75;
      for (let i = 0; i < smokeCount; i++) {
        const isSpark = Math.random() < 0.3;
        const geoPool = isSpark ? this.smokeGeometries.sparks : this.smokeGeometries.clouds;
        const geo = geoPool[Math.floor(Math.random() * geoPool.length)];
        const mesh = new THREE.Mesh(geo, this.material);

        const startX = impactX + (Math.random() - 0.5) * 1.4;
        const startY = groundY + 0.2 + Math.random() * 1.5;
        const startZ = impactZ + (Math.random() - 0.5) * 1.4;

        mesh.position.set(startX, startY, startZ);

        const ang = Math.random() * Math.PI * 2;
        const spd = 0.5 + Math.random() * 3.5;
        const vx = Math.cos(ang) * spd;
        const vy = 3.5 + Math.random() * 6.5; // buoyant upward smoke motion
        const vz = Math.sin(ang) * spd;

        const s = isSpark ? (0.8 + Math.random() * 0.8) : (1.0 + Math.random() * 1.8);
        mesh.scale.set(s, s, s);

        this.group.add(mesh);
        this.parts.push({
          mesh,
          vx, vy, vz,
          spinX: (Math.random() - 0.5) * 10,
          spinY: (Math.random() - 0.5) * 10,
          spinZ: 0,
          groundY,
          active: true,
          type: isSpark ? 'spark' : 'smoke',
          life: 0,
          maxLife: 0.9 + Math.random() * 1.2,
        });
      }

      // 2. SPAWN CHARRED SCORCH MARKS (NO BLOOD SPLATS)
      for (let i = 0; i < 4; i++) {
        const geo = this.smokeGeometries.scorches[i % this.smokeGeometries.scorches.length];
        const mesh = new THREE.Mesh(geo, this.material);
        mesh.position.set(impactX + (Math.random() - 0.5) * 1.2, groundY + 0.015, impactZ + (Math.random() - 0.5) * 1.2);
        mesh.rotation.y = Math.random() * Math.PI;
        this.group.add(mesh);
        this.parts.push({
          mesh,
          vx: 0, vy: 0, vz: 0,
          spinX: 0, spinY: 0, spinZ: 0,
          groundY,
          active: false,
          type: 'smear',
        });
      }

      while (this.parts.length > JUICE.GORE_MAX_PARTS) {
        const oldest = this.parts.shift();
        oldest.mesh.removeFromParent();
      }

      return; // Stop here — NO blood!
    }

    // 1. SPAWN DISMEMBERED BODY PARTS (8 parts)
    const bodyOffsets = [
      { x: 0,     y: 1.8, z: 0 },
      { x: 0,     y: 1.2, z: 0 },
      { x: -0.55, y: 1.3, z: 0 },
      { x: 0.55,  y: 1.3, z: 0 },
      { x: -0.28, y: 0.6, z: 0 },
      { x: 0.28,  y: 0.6, z: 0 },
      { x: -0.28, y: 0.15, z: 0 },
      { x: 0.28,  y: 0.15, z: 0 },
    ];

    for (let i = 0; i < 8; i++) {
      const geo = this.bodyPartGeometries[i];
      const mesh = new THREE.Mesh(geo, this.material);

      const off = bodyOffsets[i];
      const startX = impactX + off.x + (Math.random() - 0.5) * 0.3;
      const startY = groundY + off.y;
      const startZ = impactZ + off.z + (Math.random() - 0.5) * 0.3;

      mesh.position.set(startX, startY, startZ);

      let vx, vy, vz;
      if (isStruck) {
        // Obeying physics (x5): Body parts fly forward ahead of impact!
        vx = trainVx * (1.5 + Math.random() * 1.5) + dir * (5 + Math.random() * 10);
        vy = 12 + Math.random() * 18;
        vz = (Math.random() - 0.5) * (10 + Math.random() * 8);
      } else {
        const ang = Math.random() * Math.PI * 2;
        const spd = 2 + Math.random() * 4;
        vx = Math.cos(ang) * spd;
        vy = 5 + Math.random() * 7;
        vz = Math.sin(ang) * spd;
      }

      const spinX = (Math.random() - 0.5) * 35;
      const spinY = (Math.random() - 0.5) * 45;
      const spinZ = (Math.random() - 0.5) * 35;

      this.group.add(mesh);
      this.parts.push({
        mesh,
        vx, vy, vz,
        spinX, spinY, spinZ,
        groundY,
        active: true,
        type: 'bodypart',
        trailTimer: 0,
      });
    }

    // 2. EXPLOSIVE BLOOD BURST AROUND TRAIN FRONT (120-160 particles)
    const bloodCount = isStruck ? 150 : 40;
    for (let i = 0; i < bloodCount; i++) {
      const isSpray = Math.random() < 0.65;
      const geoPool = isSpray ? this.bloodGeometries.sprays : this.bloodGeometries.chunks;
      const geo = geoPool[Math.floor(Math.random() * geoPool.length)];
      const mesh = new THREE.Mesh(geo, this.material);

      const startX = impactX + dir * (Math.random() * 0.9) + (Math.random() - 0.5) * 0.5;
      const startY = impactY + (Math.random() - 0.5) * 1.2;
      const startZ = impactZ + (Math.random() - 0.5) * 2.4;

      mesh.position.set(startX, startY, startZ);

      let vx, vy, vz;
      if (isStruck) {
        const forwardMult = 1.3 + Math.random() * 2.2;
        vx = trainVx * forwardMult + dir * (Math.random() * 12);
        vy = Math.random() * 24;
        vz = (Math.random() - 0.5) * 22;
      } else {
        const ang = Math.random() * Math.PI * 2;
        const spd = 1 + Math.random() * 6;
        vx = Math.cos(ang) * spd;
        vy = 3 + Math.random() * 9;
        vz = Math.sin(ang) * spd;
      }

      const spinX = (Math.random() - 0.5) * 30;
      const spinY = (Math.random() - 0.5) * 30;

      const s = isSpray ? (0.8 + Math.random() * 0.9) : (1.0 + Math.random() * 1.6);
      mesh.scale.set(s, s, s);

      this.group.add(mesh);
      this.parts.push({
        mesh,
        vx, vy, vz,
        spinX, spinY, spinZ: 0,
        groundY,
        active: true,
        type: isSpray ? 'spray' : 'chunk',
      });
    }

    // 3. INITIAL HIGH-SPEED TRACK SMEARS (12-18 splats stretched forward)
    if (isStruck) {
      for (let i = 0; i < 15; i++) {
        const geo = this.bloodGeometries.splats[i % this.bloodGeometries.splats.length];
        const mesh = new THREE.Mesh(geo, this.material);

        const startX = impactX + dir * (i * 0.45 + Math.random() * 1.5);
        const startY = groundY + 0.015 + i * 0.001;
        const startZ = impactZ + (Math.random() - 0.5) * 2.0;

        mesh.position.set(startX, startY, startZ);

        const stretchX = 3.0 + Math.random() * 4.0;
        const stretchZ = 0.6 + Math.random() * 0.8;
        mesh.scale.set(stretchX, 1.0, stretchZ);

        mesh.rotation.y = (Math.random() - 0.5) * 0.2;

        this.group.add(mesh);
        this.parts.push({
          mesh,
          vx: 0, vy: 0, vz: 0,
          spinX: 0, spinY: 0, spinZ: 0,
          groundY,
          active: false,
          type: 'smear',
        });
      }
    }

    while (this.parts.length > JUICE.GORE_MAX_PARTS) {
      const oldest = this.parts.shift();
      oldest.mesh.removeFromParent();
    }
  }

  spawnBloodTrailDrop(pos, parentVx, parentVz, groundY) {
    if (this.parts.length >= JUICE.GORE_MAX_PARTS) return;
    const geo = this.bloodGeometries.sprays[Math.floor(Math.random() * this.bloodGeometries.sprays.length)];
    const mesh = new THREE.Mesh(geo, this.material);

    mesh.position.set(
      pos.x + (Math.random() - 0.5) * 0.4,
      pos.y + (Math.random() - 0.5) * 0.4,
      pos.z + (Math.random() - 0.5) * 0.4,
    );

    const vx = parentVx * 0.35 + (Math.random() - 0.5) * 2;
    const vy = -1 - Math.random() * 4;
    const vz = parentVz * 0.35 + (Math.random() - 0.5) * 2;

    this.group.add(mesh);
    this.parts.push({
      mesh,
      vx, vy, vz,
      spinX: 0, spinY: 0, spinZ: 0,
      groundY,
      active: true,
      type: 'spray',
    });
  }

  spawnGroundSplat(x, z, vx, groundY) {
    if (this.parts.length >= JUICE.GORE_MAX_PARTS) return;
    const geo = this.bloodGeometries.splats[Math.floor(Math.random() * this.bloodGeometries.splats.length)];
    const mesh = new THREE.Mesh(geo, this.material);

    mesh.position.set(x, groundY + 0.015, z);
    const stretchX = Math.min(5.0, 1.2 + Math.abs(vx) * 0.25);
    mesh.scale.set(stretchX, 1.0, 0.8 + Math.random() * 0.4);
    mesh.rotation.y = (Math.random() - 0.5) * 0.3;

    this.group.add(mesh);
    this.parts.push({
      mesh,
      vx: 0, vy: 0, vz: 0,
      spinX: 0, spinY: 0, spinZ: 0,
      groundY,
      active: false,
      type: 'smear',
    });
  }

  update(dt) {
    for (let i = this.parts.length - 1; i >= 0; i--) {
      const p = this.parts[i];
      if (!p.active) continue;

      if (p.type === 'smoke' || p.type === 'spark' || p.type === 'splash') {
        p.life = (p.life || 0) + dt;
        if (p.type === 'splash') {
          p.vy -= JUICE.GORE_GRAVITY * dt;
        } else {
          p.vy += (p.type === 'smoke' ? 1.5 : -4.0) * dt;
        }
        p.vx *= Math.max(0, 1 - 0.8 * dt);
        p.vz *= Math.max(0, 1 - 0.8 * dt);

        p.mesh.position.x += p.vx * dt;
        p.mesh.position.y += p.vy * dt;
        p.mesh.position.z += p.vz * dt;

        p.mesh.rotation.x += p.spinX * dt;
        p.mesh.rotation.y += p.spinY * dt;

        if (p.type === 'smoke') {
          p.mesh.scale.addScalar(0.75 * dt);
        }

        if (p.life >= (p.maxLife || 1.0)) {
          p.mesh.removeFromParent();
          p.active = false;
          this.parts.splice(i, 1);
        }
        continue;
      }

      p.vy -= JUICE.GORE_GRAVITY * dt;
      p.vx *= Math.max(0, 1 - JUICE.GORE_DRAG * dt);
      p.vz *= Math.max(0, 1 - JUICE.GORE_DRAG * dt);

      p.mesh.position.x += p.vx * dt;
      p.mesh.position.y += p.vy * dt;
      p.mesh.position.z += p.vz * dt;

      p.mesh.rotation.x += p.spinX * dt;
      p.mesh.rotation.y += p.spinY * dt;
      if (p.spinZ) p.mesh.rotation.z += p.spinZ * dt;

      // Drop blood trails from airborne body parts
      if (p.type === 'bodypart' && p.mesh.position.y > p.groundY + 0.5) {
        p.trailTimer = (p.trailTimer || 0) + dt;
        if (p.trailTimer > 0.035) {
          p.trailTimer = 0;
          this.spawnBloodTrailDrop(p.mesh.position, p.vx, p.vz, p.groundY);
        }
      }

      const targetY = p.groundY + (p.type === 'smear' ? 0.015 : 0.04);
      if (p.mesh.position.y <= targetY) {
        if (p.type === 'bodypart') {
          p.mesh.position.y = targetY;
          p.vy = -p.vy * 0.35;
          p.vx *= 0.55;
          p.vz *= 0.55;
          p.spinX *= 0.5;
          p.spinY *= 0.5;
          p.spinZ *= 0.5;

          if (Math.abs(p.vy) > 1.0 || Math.abs(p.vx) > 2.0) {
            this.spawnGroundSplat(p.mesh.position.x, p.mesh.position.z, p.vx, p.groundY);
          }

          if (Math.abs(p.vy) < 0.3 && Math.abs(p.vx) < 0.3 && Math.abs(p.vz) < 0.3) {
            p.mesh.position.y = targetY;
            p.vy = 0; p.vx = 0; p.vz = 0;
            p.spinX = 0; p.spinY = 0; p.spinZ = 0;
            p.active = false;
          }
        } else if (p.type === 'chunk' || p.type === 'spray') {
          p.mesh.position.y = p.groundY + 0.015 + (Math.random() * 0.005);
          p.vy = 0;

          const speedX = Math.abs(p.vx);
          const stretchX = Math.min(5.0, 1.0 + speedX * 0.25);
          const stretchZ = 0.8 + Math.random() * 0.6;
          p.mesh.scale.set(stretchX, 0.05, stretchZ);

          p.mesh.rotation.x = 0;
          p.mesh.rotation.z = 0;
          p.mesh.rotation.y = (Math.random() - 0.5) * 0.3;

          p.vx = 0; p.vz = 0;
          p.spinX = 0; p.spinY = 0; p.spinZ = 0;
          p.active = false;
        }
      }
    }
  }

  clear() {
    for (const p of this.parts) {
      p.mesh.removeFromParent();
    }
    this.parts = [];
  }

  dispose() {
    this.unsub.forEach((u) => u());
    this.clear();
    this.group.removeFromParent();
    for (const g of this.bodyPartGeometries) g.dispose();
    for (const g of this.bloodGeometries.chunks) g.dispose();
    for (const g of this.bloodGeometries.sprays) g.dispose();
    for (const g of this.bloodGeometries.splats) g.dispose();
    for (const g of this.smokeGeometries.clouds) g.dispose();
    for (const g of this.smokeGeometries.sparks) g.dispose();
    for (const g of this.smokeGeometries.scorches) g.dispose();
    this.material.dispose();
  }
}
